import { useCallback, useMemo, useState } from 'react';

import { useMaterials } from '~/api/easy-estimator-api/materials-api/hooks/useMaterials';
import { useSelectedMaterial } from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { useEquivalentProductMatching } from '~/core/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { useResetSteps } from '~/core/hooks/useResetSteps';
import type { MaterialModel } from '~/core/models';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectColorsByProductId,
  selectSelectedColor,
  updateSelectedColorId,
} from '~/redux/slices/color';
import { updateSelectedMaterial } from '~/redux/slices/material';
import { useDrawingShapeContext } from '~contexts';
import { MaterialCardData } from '~molecules/MaterialCard';
import {
  RemovedProduct,
  collectRemovedProducts,
} from '~organisms/ProductReplacementModal';

type OverwritingMaterial =
  | {
      id: MaterialModel['id'];
      name: string;
    }
  | undefined;

export const useMaterialPage = () => {
  const [candidateMaterial, setCandidateMaterial] =
    useState<OverwritingMaterial>();
  const [removedProducts, setRemovedProducts] = useState<RemovedProduct[]>([]);

  const { isFetchingMaterials, materials } = useMaterials();
  const selectedMaterial = useSelectedMaterial();
  const {
    getters: { areThereShapesInTheDrawingArea },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();
  const { restoreDataWithoutAutoNavigation } = useResetSteps();
  const {
    replaceProductsWithEquivalents,
    resolveAndMakeProductMatchingSummary,
  } = useEquivalentProductMatching();

  const dispatch = useAppDispatch();

  const selectedColor = useAppSelector(selectSelectedColor);
  const colorsById = useAppSelector(selectColorsByProductId);

  const materialsList: MaterialCardData[] = useMemo(
    () =>
      materials.map((material) => ({
        id: material.id,
        img: { src: material.imageUrl ?? '' },
        title: material.description ?? '',
        priceRange: {
          min: material.minPrice ?? 0,
          max: material.maxPrice ?? 0,
        },
        specifications: material.attributes.map((attr) => ({
          title: attr.description,
          rating: attr.score,
        })),
        qteGrpId: material.qteGrpID,
      })),
    [materials]
  );

  const changeSelectedMaterial = useCallback(
    (material?: MaterialModel) => {
      restoreDataWithoutAutoNavigation(() => {
        if (material) {
          dispatch(updateSelectedMaterial(material));
        }
      });
    },
    [dispatch, restoreDataWithoutAutoNavigation]
  );

  const closeModal = useCallback(() => {
    setCandidateMaterial(undefined);
    setRemovedProducts([]);
  }, []);

  const selectCandidateMaterial = useCallback(
    async (overwritingMaterialId: MaterialModel['id']) => {
      const material = materials.find(({ id }) => id === overwritingMaterialId);

      if (material) {
        // Do product matching first to get the updated shapes and matched color
        const result = await replaceProductsWithEquivalents({ material });

        if (!result) {
          changeSelectedMaterial(material);
          closeModal();

          return;
        }

        const { shapes: updatedShapes, validatedParams } = result;

        // Update material and color together - this ensures color is preserved
        restoreDataWithoutAutoNavigation(() => {
          dispatch(updateSelectedMaterial(material));
          // Update the selected color to the matched color from the new material
          if (validatedParams.color) {
            dispatch(updateSelectedColorId(validatedParams.color.productId));
          }
        });

        // Re-set the shapes after material/color update to ensure they persist through any effects
        // Use a small delay to let any effects from material/color update complete
        if (updatedShapes && updatedShapes.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, 10));
          updateDrawingShapes([...updatedShapes]);
        }
      } else {
        changeSelectedMaterial();
      }

      closeModal();
    },
    [
      materials,
      changeSelectedMaterial,
      closeModal,
      replaceProductsWithEquivalents,
      dispatch,
      restoreDataWithoutAutoNavigation,
      updateDrawingShapes,
    ]
  );

  /**
   * Returns a list of products that will change based on the input value
   * @param input The product that triggers a change, being a 'material' or 'color'
   * @returns the list of products to remove
   */
  const getRemovedProductsFromProductChange = useCallback(
    async (
      input: Parameters<typeof resolveAndMakeProductMatchingSummary>[0]
    ) => {
      const summary = await resolveAndMakeProductMatchingSummary(input);

      return summary ? collectRemovedProducts(summary) : [];
    },
    [resolveAndMakeProductMatchingSummary]
  );

  /**
   * Sets a list of product replacements 'removedProducts' based on the change of a color.
   * @param productId - The 'productId' of the color that changed.
   */
  const findAndSetRemovedProductsByColorId = useCallback(
    async (productId: number) => {
      const color = colorsById[productId];

      if (color.productId === selectedColor?.productId) {
        return [];
      }

      const removedProducts = await getRemovedProductsFromProductChange({
        color,
      });

      setRemovedProducts(removedProducts);

      return removedProducts;
    },
    [colorsById, selectedColor?.productId, getRemovedProductsFromProductChange]
  );

  const selectMaterialId = useCallback(
    async (materialId: string) => {
      const material = materials.find(({ id }) => id === materialId);

      if (material?.id === selectedMaterial?.id) {
        return;
      }

      if (!areThereShapesInTheDrawingArea) {
        changeSelectedMaterial(material);

        return;
      }

      const removedProducts = await getRemovedProductsFromProductChange({
        material,
      });

      if (removedProducts.length) {
        setRemovedProducts(removedProducts);
        setCandidateMaterial({
          id: materialId,
          name: material?.description ?? '',
        });
      } else {
        selectCandidateMaterial(materialId);
      }
    },
    [
      materials,
      selectedMaterial?.id,
      areThereShapesInTheDrawingArea,
      changeSelectedMaterial,
      selectCandidateMaterial,
      getRemovedProductsFromProductChange,
    ]
  );

  return {
    isFetchingMaterials,
    materialsList,
    selectedMaterial,
    findAndSetRemovedProductsByColorId,
    selectMaterialId,
    closeModal,
    candidateMaterial,
    selectCandidateMaterial,
    removedProducts,
  };
};
